import React, { Fragment, isValidElement, cloneElement, type ReactNode, Children, type FC } from 'react';
import { sanitizeUrl } from '../../utils/url-utils';

interface LinkifyProps {
    children: ReactNode;
}

// A simple URL regex matching http(s):// or www.…
const URL_REGEX = /(\b(?:https?:\/\/|www\.)[^\s]+)/gi;

const Linkify: FC<LinkifyProps> = props => {
    const { children } = props;

    const processChild = (child: ReactNode, key: number | string): ReactNode => {
        if (typeof child === 'string' || typeof child === 'number') {
            const text = String(child);

            if (text === '') return child; // Render empty string as is (or could return null)

            const parts = text.split(URL_REGEX);

            return (
                <Fragment key={key}>
                    {parts.map((part, i) => {
                        if (part === '') {
                            return null;
                        }

                        const isUrlPart = i % 2 === 1;

                        if (isUrlPart) {
                            const potentialHref = part.match(/^https?:\/\//i) ? part : `http://${part}`;
                            const finalHref = sanitizeUrl(potentialHref);

                            if (finalHref) {
                                return (
                                    <a key={part} href={finalHref} target="_blank" rel="noopener noreferrer">
                                        {part}
                                    </a>
                                );
                            }
                        }

                        return <Fragment key={part}>{part}</Fragment>;
                    })}
                </Fragment>
            );
        }

        if (isValidElement(child) && child.props.children) {
            return cloneElement(child, { key }, processChildren(child.props.children));
        }

        return child;
    };

    const processChildren = (nodes: ReactNode): ReactNode => {
        return Children.map(nodes, (child, i) => processChild(child, i)) || null;
    };

    return <>{processChildren(children)}</>;
};

export default Linkify;
